import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Home,
  Wallet,
  Briefcase,
  HeartPulse,
  Users,
  Handshake,
  ChevronRight,
  Settings,
  Sparkles,
  Cpu,
  History,
} from "lucide-react";
import { PILLARS } from "@/data/pillars";
import { useAuth } from "@/context/AuthContext";
import { APP_TITLE, APP_SUBTITLE } from "@/lib/brand";

const ICONS = { Home, Wallet, Briefcase, HeartPulse, Users, Handshake };

function PillarNav({ pillar, onNavigate }) {
  const location = useLocation();
  const Icon = ICONS[pillar.icon];
  const isActive = location.pathname.startsWith(`/pillar/${pillar.id}`);
  const [open, setOpen] = useState(isActive);

  const subItems = [
    { label: "Summary", to: `/pillar/${pillar.id}`, end: true },
    { label: "Goals", to: `/pillar/${pillar.id}/goals`, end: true },
    ...(pillar.id === "finance"
      ? [{ label: "Asset Management", to: `/pillar/finance/assets`, end: false }]
      : []),
    ...(pillar.id === "home"
      ? [{ label: "To-Do", to: `/pillar/home/todos`, end: true }]
      : []),
    ...(pillar.id === "health"
      ? [{ label: "My Health", to: `/pillar/health/my-health`, end: false }]
      : []),
    ...(pillar.id === "work"
      ? [{ label: "Travel", to: `/pillar/work/travel`, end: false }]
      : []),
    ...(pillar.id === "family" || pillar.id === "friends"
      ? [{ label: "Calendar", to: `/pillar/${pillar.id}/calendar`, end: true }]
      : []),
  ];

  return (
    <div className="select-none">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`sidebar-pillar-${pillar.id}`}
        className="w-full group flex items-center gap-3 px-3 py-2 rounded-md text-[15px] text-foreground/80 hover:bg-secondary transition-colors duration-200"
      >
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: pillar.color }}
        />
        <Icon className="w-4 h-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        <span className="flex-1 text-left">{pillar.name}</span>
        <ChevronRight
          className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
            open ? "rotate-90" : ""
          }`}
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: open ? subItems.length * 40 + "px" : "0px" }}
      >
        <div className="pl-8 pr-2 py-1 space-y-0.5">
          {subItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`sidebar-sub-${pillar.id}-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive: a }) =>
                `block px-3 py-1.5 text-sm rounded-md transition-colors duration-200 ${
                  a
                    ? "bg-secondary text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Sidebar({ onNavigate }) {
  const { user } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6">
        <h1 className="font-serif text-2xl leading-tight text-foreground" data-testid="app-title">
          {APP_TITLE}
        </h1>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">
          {APP_SUBTITLE}
        </p>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto quiet-scroll">
        <NavLink
          to="/"
          end
          onClick={onNavigate}
          data-testid="sidebar-dashboard"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md text-[15px] transition-colors duration-200 ${
              isActive
                ? "bg-secondary text-foreground font-medium"
                : "text-foreground/80 hover:bg-secondary"
            }`
          }
        >
          <span className="w-2.5 h-2.5" />
          <LayoutDashboard className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          <span>Dashboard</span>
        </NavLink>

        <NavLink
          to="/reflections"
          end
          onClick={onNavigate}
          data-testid="sidebar-reflections"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md text-[15px] transition-colors duration-200 ${
              isActive ? "bg-secondary text-foreground font-medium" : "text-foreground/80 hover:bg-secondary"
            }`
          }
        >
          <span className="w-2.5 h-2.5" />
          <History className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          <span>Reflections</span>
        </NavLink>

        <div className="pt-3 pb-1 px-3">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70">
            Pillars
          </span>
        </div>

        {PILLARS.map((pillar) => (
          <PillarNav key={pillar.id} pillar={pillar} onNavigate={onNavigate} />
        ))}

        {user?.role === "admin" && (
          <>
            <div className="pt-3 pb-1 px-3">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70">
                Admin
              </span>
            </div>
            <NavLink
              to="/admin/ai"
              end
              onClick={onNavigate}
              data-testid="sidebar-admin-ai"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-[15px] transition-colors duration-200 ${
                  isActive ? "bg-secondary text-foreground font-medium" : "text-foreground/80 hover:bg-secondary"
                }`
              }
            >
              <span className="w-2.5 h-2.5" />
              <Sparkles className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
              <span>AI</span>
            </NavLink>
            <NavLink
              to="/admin/device-types"
              end
              onClick={onNavigate}
              data-testid="sidebar-admin-device-types"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-[15px] transition-colors duration-200 ${
                  isActive ? "bg-secondary text-foreground font-medium" : "text-foreground/80 hover:bg-secondary"
                }`
              }
            >
              <span className="w-2.5 h-2.5" />
              <Cpu className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
              <span>Device Types</span>
            </NavLink>
          </>
        )}

        <div className="pt-3 pb-1 px-3">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70">
            Account
          </span>
        </div>
        <NavLink
          to="/settings"
          end
          onClick={onNavigate}
          data-testid="sidebar-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md text-[15px] transition-colors duration-200 ${
              isActive ? "bg-secondary text-foreground font-medium" : "text-foreground/80 hover:bg-secondary"
            }`
          }
        >
          <span className="w-2.5 h-2.5" />
          <Settings className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          <span>Settings</span>
        </NavLink>
      </nav>
    </div>
  );
}
